import React, { useState, useRef } from "react";
import { Upload, Trash2, FileText, Image as ImageIcon, Eye, Download, AlertCircle, IdCard, Building2, User } from "lucide-react";
import api, { API, getToken } from "@/lib/api";
import { useT } from "@/lib/i18n";

const ALLOWED = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp", "image/heic", "image/heif", "image/gif"];
const MAX_BYTES = 10 * 1024 * 1024;

function humanSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / 1024 / 1024).toFixed(2) + " MB";
}

function isImage(mime) { return (mime || "").startsWith("image/"); }

async function fetchBlobUrl(empId, attId) {
  const token = getToken();
  const res = await fetch(`${API}/employees/${empId}/attachments/${attId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("download failed");
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

function SlotCard({ slot, item, onUpload, onView, onDownload, onRemove, t, uploading }) {
  const inputRef = useRef(null);
  const Icon = slot.icon;
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`attach-slot-${slot.kind}`}>
      <div className={`px-4 py-3 flex items-center justify-between ${slot.bg}`}>
        <div className="flex items-center gap-2">
          <Icon size={16} className={slot.iconColor} />
          <span className="text-sm font-bold text-gray-900">{slot.title}</span>
        </div>
        {item && <span className="pill status-valid text-[10px]">✓</span>}
      </div>

      <div className="p-3">
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpload(f, slot.kind);
            if (inputRef.current) inputRef.current.value = "";
          }}
          data-testid={`attach-input-${slot.kind}`}
        />

        {item ? (
          <div>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg mb-2">
              <div className={`rounded p-1.5 ${isImage(item.content_type) ? "bg-indigo-100 text-indigo-700" : "bg-rose-100 text-rose-700"}`}>
                {isImage(item.content_type) ? <ImageIcon size={14} /> : <FileText size={14} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate">{item.filename}</div>
                <div className="text-[10px] text-gray-500">{humanSize(item.size)}</div>
              </div>
            </div>
            <div className="flex gap-1">
              <button type="button" className="btn btn-ghost flex-1 text-xs" onClick={() => onView(item)} data-testid={`view-${slot.kind}`}>
                <Eye size={12} /> {t("view_file")}
              </button>
              <button type="button" className="btn btn-ghost flex-1 text-xs" onClick={() => onDownload(item)} data-testid={`download-${slot.kind}`}>
                <Download size={12} />
              </button>
              <button type="button" className="btn btn-danger text-xs" onClick={() => onRemove(item)} data-testid={`delete-${slot.kind}`}>
                <Trash2 size={12} />
              </button>
              <button type="button" className="btn btn-secondary text-xs" onClick={() => inputRef.current?.click()} data-testid={`replace-${slot.kind}`} title="استبدال">
                <Upload size={12} />
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="w-full border-2 border-dashed border-gray-300 hover:border-teal-400 rounded-lg p-4 text-center transition cursor-pointer"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`upload-${slot.kind}`}
          >
            <Upload size={20} className="mx-auto text-gray-400 mb-1" />
            <span className="text-xs text-gray-600">{uploading ? t("uploading") : t("upload_file")}</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default function AttachmentsPanel({ employeeId, attachments: initial, onChange }) {
  const { t } = useT();
  const [items, setItems] = useState(initial || []);
  const [uploadingKind, setUploadingKind] = useState(null);
  const [err, setErr] = useState("");

  const slots = [
    { kind: "municipality_card", title: t("attach_municipality"), icon: Building2, iconColor: "text-emerald-600", bg: "bg-emerald-50" },
    { kind: "id_card", title: t("attach_id"), icon: IdCard, iconColor: "text-indigo-600", bg: "bg-indigo-50" },
    { kind: "personal_photo", title: t("attach_personal"), icon: User, iconColor: "text-amber-600", bg: "bg-amber-50" },
  ];

  const findByKind = (kind) => items.find((x) => x.kind === kind);
  const knownKinds = new Set(["municipality_card", "id_card", "personal_photo"]);
  const legacy = items.filter((x) => !knownKinds.has(x.kind));

  const refresh = (newList) => {
    setItems(newList);
    onChange && onChange(newList);
  };

  const upload = async (file, kind) => {
    setErr("");
    if (!ALLOWED.includes(file.type)) { setErr(t("invalid_file_type")); return; }
    if (file.size > MAX_BYTES) { setErr(t("file_too_large")); return; }
    setUploadingKind(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const res = await api.post(`/employees/${employeeId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filtered = items.filter((x) => x.kind !== kind);
      refresh([...filtered, res.data]);
    } catch (e) {
      setErr(e.response?.data?.detail || t("upload_failed"));
    } finally {
      setUploadingKind(null);
    }
  };

  const view = async (att) => {
    try {
      const url = await fetchBlobUrl(employeeId, att.id);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { setErr(t("upload_failed")); }
  };

  const download = async (att) => {
    try {
      const url = await fetchBlobUrl(employeeId, att.id);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { setErr(t("upload_failed")); }
  };

  const remove = async (att) => {
    if (!window.confirm(t("confirm_delete_file", { name: att.filename }))) return;
    try {
      await api.delete(`/employees/${employeeId}/attachments/${att.id}`);
      refresh(items.filter((x) => x.id !== att.id));
    } catch (e) {
      setErr(e.response?.data?.detail || t("upload_failed"));
    }
  };

  if (!employeeId) {
    return (
      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
        <AlertCircle size={14} /> {t("save_first")}
      </div>
    );
  }

  return (
    <div data-testid="attachments-panel">
      <p className="text-xs text-gray-500 mb-3">{t("attachments_subtitle")}</p>
      {err && (
        <div className="status-expired pill px-3 py-2 mb-3 text-xs">{err}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {slots.map((slot) => (
          <SlotCard
            key={slot.kind}
            slot={slot}
            item={findByKind(slot.kind)}
            onUpload={upload}
            onView={view}
            onDownload={download}
            onRemove={remove}
            t={t}
            uploading={uploadingKind === slot.kind}
          />
        ))}
      </div>

      {legacy.length > 0 && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="legacy-attachments">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={14} className="text-amber-700" />
            <h4 className="text-sm font-bold text-amber-900">{t("legacy_attachments_title")}</h4>
          </div>
          <p className="text-[11px] text-amber-700 mb-2">{t("legacy_attachments_hint")}</p>
          <ul className="space-y-2">
            {legacy.map((att) => (
              <li key={att.id} className="flex items-center gap-2 bg-white rounded-lg px-2 py-1.5" data-testid={`legacy-row-${att.id}`}>
                <div className={`rounded p-1.5 ${isImage(att.content_type) ? "bg-indigo-100 text-indigo-700" : "bg-rose-100 text-rose-700"}`}>
                  {isImage(att.content_type) ? <ImageIcon size={12} /> : <FileText size={12} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">{att.filename}</div>
                  <div className="text-[10px] text-gray-500">{humanSize(att.size)}</div>
                </div>
                <button type="button" className="btn btn-ghost text-xs" onClick={() => view(att)} data-testid={`legacy-view-${att.id}`}>
                  <Eye size={11} />
                </button>
                <button type="button" className="btn btn-ghost text-xs" onClick={() => download(att)} data-testid={`legacy-download-${att.id}`}>
                  <Download size={11} />
                </button>
                <button type="button" className="btn btn-danger text-xs" onClick={() => remove(att)} data-testid={`legacy-delete-${att.id}`}>
                  <Trash2 size={11} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
